/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import MsDyn365, { ContentEditableEvent, msdyn365Commerce, RichTextComponent, Text } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import uuid from 'uuid';
import { mFIEmailSubscriptionAsync } from '../../actions/EmailSubscriptionDataActionExtension.g';
import { IMFIEmailSubscription } from '../../actions/EmailSubscriptionDataServiceEntities.g';
import { emailRegex } from '../../Utilities/allRegex';
import { IMfrmFooterEmailSubscriptionData } from './mfrm-footer-email-subscription.data';
import { IMfrmFooterEmailSubscriptionProps } from './mfrm-footer-email-subscription.props.autogenerated';

export interface IMfrmFooterEmailSubscriptionViewProps extends IMfrmFooterEmailSubscriptionProps<IMfrmFooterEmailSubscriptionData> {
    title: JSX.Element;
    form: JSX.Element;
    alertMessage: JSX.Element;
    className: string;
}

interface IFooterEmailSubscriptionState {
    isSignedUpSuccess: boolean;
    // tslint:disable-next-line: no-any
    errorMsg: any;
    isError: boolean;
    emailValue: string;
}

/**
 *
 * MfrmFooterEmailSubscription component
 * @extends {React.PureComponent<IMfrmFooterEmailSubscriptionProps<IMfrmFooterEmailSubscriptionData>, IFooterEmailSubscriptionState>}
 */
class MfrmFooterEmailSubscription extends React.PureComponent<
    IMfrmFooterEmailSubscriptionProps<IMfrmFooterEmailSubscriptionData>,
    IFooterEmailSubscriptionState
> {
    private _successMsgTimeOut: number = 6000;

    constructor(props: IMfrmFooterEmailSubscriptionProps<IMfrmFooterEmailSubscriptionData>) {
        super(props);
        this.state = {
            isSignedUpSuccess: false,
            errorMsg: '',
            isError: false,
            emailValue: ''
        };
        this.onFinished = this.onFinished.bind(this);
        this.handleChange = this.handleChange.bind(this);
        if (msdyn365Commerce.isBrowser) {
            document.documentElement.style.setProperty('--ftr-msg-timeout-duration', `${this._successMsgTimeOut}`);
        }
    }

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            ...this.state,
            title: this._renderTitle,
            form: this._mailingListJSX,
            alertMessage: this._renderAlertMessage,
            className: this.props.config.className
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private get _mailingListJSX(): JSX.Element {
        const { emailLabel, emailPlaceholder } = this.props.resources;
        const { emailValue, errorMsg, isError } = this.state;
        return (
            <form className='ms-footer__email-subscription__form' onSubmit={this.onFinished} noValidate={true}>
                <div className='field-group'>
                    <input
                        type='text'
                        className={classnames(
                            'field-control',
                            emailValue.length > 0 && 'field-control--value',
                            errorMsg.length > 0 && isError && 'field-control--error'
                        )}
                        value={this.state.emailValue}
                        placeholder={emailPlaceholder}
                        onChange={this.handleChange}
                    />
                    <label className='field-label'>{emailLabel}</label>
                </div>
                <button className='ms-footer__email-subscription__button' type='submit'>
                    {this.props.config.submitCTAText || 'Submit'}
                </button>
            </form>
        );
    }

    private get _renderTitle(): JSX.Element {
        const { heading } = this.props.config;
        return <Text className='ms-footer__email-subscription__title' tag={heading!.tag || 'h2'} text={heading!.text} />;
    }

    private get _renderAlertMessage(): JSX.Element | null {
        const { isError, isSignedUpSuccess, errorMsg } = this.state;
        const { successText } = this.props.config;
        if (isError || isSignedUpSuccess) {
            return (
                <div className='ms-footer__email-subscription__alert-message'>
                    {isSignedUpSuccess && (
                        <RichTextComponent className='ms-footer__email-subscription__alert-message--success' text={successText as string} />
                    )}
                    {isError && (
                        <RichTextComponent className='ms-footer__email-subscription__alert-message--error' text={errorMsg as string} />
                    )}
                </div>
            );
        }
        return null;
    }
    public handleTextChange = (event: ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    public onFinished = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const { emailValue } = this.state;

        if (emailValue === '' || !emailRegex(emailValue)) {
            this.setState({
                isError: true,
                errorMsg: `<p>${this.props.resources.emailValidation}</p>`,
                isSignedUpSuccess: false
            });
        } else {
            const { errorText, showErrorException } = this.props.config;
            const emailSignup: IMFIEmailSubscription = {
                PersonaSleepStyle: '',
                unique_id: uuid.v4(),
                Email: emailValue,
                LeadSource: 'footer',
                PersonaMattressPreference: '',
                Lastname: '',
                LeadDate: new Date().toISOString(),
                Firstname: '',
                PostalCode: '',
                LeadPage: this.props.context.request.telemetryPageName?.substring(0,120),
                LeadPlatform: 'D365'
            };

            try {
                const response = await mFIEmailSubscriptionAsync({ callerContext: this.props.context.actionContext }, emailSignup);
                if (response.Status === 'Success') {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: this.props.config.tealiumEvent?.event ? this.props.config.tealiumEvent.event : 'footer-email-signup',
                            event_category: this.props.config.tealiumEvent?.event_category ? this.props.config.tealiumEvent.event_category : 'email signup tracking',
                            event_action: this.props.config.tealiumEvent?.event_action ? this.props.config.tealiumEvent.event_action : `click ${this.props.config.submitCTAText || 'Submit'}`,
                            event_label: this.props.config.tealiumEvent?.event_label ? this.props.config.tealiumEvent.event_label : `from footer: ${MsDyn365.isBrowser && window.location.href}`,
                            event_noninteraction: 'false',
                            event_type: this.props.config.tealiumEvent?.event_type ? this.props.config.tealiumEvent.event_type : 'email_capture',
                            customer_email: emailValue
                        });
                    }
                    this.setState({ isSignedUpSuccess: true, errorMsg: '', isError: false, emailValue: '' });
                    MsDyn365.isBrowser && setTimeout(() => this.setState({ isSignedUpSuccess: false }), this._successMsgTimeOut);
                } else {
                    this.setState({
                        isError: true,
                        errorMsg: showErrorException ? response.Message : errorText,
                        isSignedUpSuccess: false
                    });
                }
            } catch (error) {
                this.setState({
                    isError: true,
                    errorMsg: showErrorException ? error : errorText,
                    isSignedUpSuccess: false
                });
            }
        }
    };

    public handleChange = (e: React.FormEvent<HTMLInputElement>): void => {
        const currentlyInvalid = this.state.emailValue.length > 0 && !emailRegex(this.state.emailValue);
        if (currentlyInvalid) {
            this.setState({ emailValue: e.currentTarget.value, isError: false });
        } else {
            this.setState({ emailValue: e.currentTarget.value, isError: true });
        }
    };
}

export default MfrmFooterEmailSubscription;
